// ═════════════════════════════════════════════════════════════════════════════
// moveTree.js — Motor local del árbol de variantes (Chess4Kings — Etapa 1)
// ═════════════════════════════════════════════════════════════════════════════
//
// Módulo de lógica PURA y AISLADA. Sin dependencias externas, sin efectos
// secundarios, sin estado de React, sin llamadas API. Todas las funciones son
// inmutables: reciben un árbol y devuelven un árbol nuevo (nunca mutan).
//
// Estructura del árbol:
//
//   {
//     rootId: 'root',
//     currentId: <string>,
//     nodes: {
//       [nodeId]: {
//         id:          string,
//         parentId:    string | null,     // null solo en la raíz
//         childrenIds: string[],           // orden = childrenIds[0] es la mainline
//         move: {                          // null solo en la raíz
//           san:        string,
//           fen:        string,            // FEN DESPUÉS de aplicar la jugada
//           from:       string,
//           to:         string,
//           promotion?: 'q'|'r'|'b'|'n',
//         } | null,
//         // Solo la raíz almacena `fen` directamente (posición inicial).
//         // Para nodos no-raíz, el FEN vive en `move.fen`.
//         fen?: string,
//       }
//     }
//   }
//
// La mainline desde cualquier nodo se define recursivamente como
// `childrenIds[0]` (el primer hijo). Cuando se agrega una jugada nueva se
// añade AL FINAL de `childrenIds` — nunca desplaza a la mainline existente.
//
// El módulo NO conoce las reglas del ajedrez ni valida movimientos. Recibe
// jugadas ya validadas por el consumidor (ClassroomLive usará chess.js).
// ═════════════════════════════════════════════════════════════════════════════

// -----------------------------------------------------------------------------
// Utilidades internas
// -----------------------------------------------------------------------------

// Generador de IDs monotónicos por proceso. Colisiones prácticamente
// imposibles a escala de una sesión de aula (millones/hora). No usamos
// crypto.randomUUID para mantener compatibilidad con entornos sin él.
let _idCounter = 0;
function _newId() {
  _idCounter += 1;
  return `n${Date.now().toString(36)}_${_idCounter.toString(36)}`;
}

// Detecta si dos jugadas son equivalentes (mismo movimiento desde el mismo
// padre). Se usa para deduplicar en addMove.
function _movesEqual(a, b) {
  if (!a || !b) return false;
  // Comparación estricta por SAN + from + to (+ promoción si aplica).
  // No comparamos por FEN porque una misma SAN desde el mismo padre siempre
  // produce el mismo FEN — SAN es suficiente y evita depender del formato
  // exacto del FEN (halfmove/fullmove counters, etc.).
  if (a.san !== b.san) return false;
  if (a.from !== b.from) return false;
  if (a.to !== b.to) return false;
  if ((a.promotion || null) !== (b.promotion || null)) return false;
  return true;
}

// -----------------------------------------------------------------------------
// API pública
// -----------------------------------------------------------------------------

/**
 * Crea un árbol nuevo con solamente el nodo raíz.
 *
 * @param {string} startingFen  FEN de la posición inicial.
 * @returns {object}            Árbol nuevo con root=current.
 */
export function createTree(startingFen) {
  const rootId = 'root';
  return {
    rootId,
    currentId: rootId,
    nodes: {
      [rootId]: {
        id: rootId,
        parentId: null,
        childrenIds: [],
        move: null,
        fen: startingFen,
      },
    },
  };
}

/**
 * Alias semántico de createTree. Devuelve un árbol vacío recién creado; el
 * consumidor descarta el árbol previo llamando esta función.
 *
 * @param {string} startingFen  FEN de la posición inicial.
 * @returns {object}            Árbol nuevo con root=current.
 */
export function clearTree(startingFen) {
  return createTree(startingFen);
}

/**
 * Devuelve un nodo por su id, o undefined si no existe.
 *
 * @param {object} tree
 * @param {string} nodeId
 * @returns {object|undefined}
 */
export function getNode(tree, nodeId) {
  if (!tree || !tree.nodes) return undefined;
  return tree.nodes[nodeId];
}

/**
 * Devuelve los hijos directos del nodo en el orden guardado (mainline primero).
 *
 * @param {object} tree
 * @param {string} nodeId
 * @returns {object[]}          Array vacío si el nodo no existe o no tiene hijos.
 */
export function getChildren(tree, nodeId) {
  const node = getNode(tree, nodeId);
  if (!node) return [];
  return node.childrenIds
    .map(cid => tree.nodes[cid])
    .filter(Boolean);
}

/**
 * Devuelve el nodo padre, o undefined si el nodo es la raíz o no existe.
 *
 * @param {object} tree
 * @param {string} nodeId
 * @returns {object|undefined}
 */
export function getParent(tree, nodeId) {
  const node = getNode(tree, nodeId);
  if (!node || node.parentId == null) return undefined;
  return tree.nodes[node.parentId];
}

/**
 * Devuelve una copia superficial del árbol con currentId actualizado.
 * NO muta el árbol original. Si el nodeId no existe, devuelve el árbol
 * original sin cambios (comportamiento defensivo, evita romper el estado).
 *
 * @param {object} tree
 * @param {string} nodeId
 * @returns {object}
 */
export function navigateTo(tree, nodeId) {
  if (!getNode(tree, nodeId)) return tree;
  if (tree.currentId === nodeId) return tree;
  return {
    ...tree,
    currentId: nodeId,
  };
}

/**
 * Agrega una jugada como hijo del nodo `parentId`. Reglas:
 *   - No borra hijos existentes.
 *   - Permite varias jugadas alternativas desde el mismo padre.
 *   - Reutiliza un hijo existente si la jugada es equivalente (mismo SAN +
 *     from + to + promotion) — actualiza currentId al hijo existente.
 *   - En caso contrario, crea un nodo nuevo, lo añade al FINAL de
 *     childrenIds del padre y actualiza currentId al nodo nuevo.
 *   - NO muta el árbol original.
 *
 * @param {object} tree
 * @param {string} parentId
 * @param {object} move   { san, fen, from, to, promotion? }
 * @returns {object}      Árbol nuevo.
 */
export function addMove(tree, parentId, move) {
  const parent = getNode(tree, parentId);
  if (!parent) {
    // Padre inexistente: fallback defensivo, no muta.
    return tree;
  }
  if (!move || typeof move.san !== 'string' || typeof move.fen !== 'string') {
    // Movimiento inválido: no toca nada.
    return tree;
  }

  // 1. ¿Existe ya un hijo equivalente? Si sí, reutilizar.
  for (const cid of parent.childrenIds) {
    const child = tree.nodes[cid];
    if (child && _movesEqual(child.move, move)) {
      // Reutilizar: sólo cambia currentId, sin mutar nada más.
      return {
        ...tree,
        currentId: cid,
      };
    }
  }

  // 2. Crear nodo nuevo, insertarlo al final de childrenIds del padre.
  const newId = _newId();
  const newNode = {
    id: newId,
    parentId: parent.id,
    childrenIds: [],
    move: {
      san: move.san,
      fen: move.fen,
      from: move.from,
      to: move.to,
      // Solo incluir promoción si viene definida.
      ...(move.promotion ? { promotion: move.promotion } : {}),
    },
  };
  const updatedParent = {
    ...parent,
    childrenIds: [...parent.childrenIds, newId],
  };
  return {
    ...tree,
    currentId: newId,
    nodes: {
      ...tree.nodes,
      [parent.id]: updatedParent,
      [newId]: newNode,
    },
  };
}

/**
 * Devuelve la ruta desde la raíz hasta el nodo indicado, en orden raíz→hoja.
 * Incluye el nodo raíz como primer elemento y el nodo indicado como último.
 *
 * @param {object} tree
 * @param {string} nodeId
 * @returns {object[]}          Array vacío si el nodo no existe.
 */
export function getPath(tree, nodeId) {
  const target = getNode(tree, nodeId);
  if (!target) return [];
  const path = [];
  let cursor = target;
  // Ascender por parentId hasta la raíz.
  while (cursor) {
    path.unshift(cursor);
    if (cursor.parentId == null) break;
    cursor = tree.nodes[cursor.parentId];
    if (!cursor) break; // integridad rota — devolver lo que llevamos.
  }
  return path;
}

/**
 * Devuelve la variante principal del árbol: raíz → childrenIds[0] recursivo
 * hasta llegar a una hoja. La mainline SIEMPRE parte de la raíz por diseño.
 *
 * @param {object} tree
 * @returns {object[]}          Array de nodos, empezando por la raíz.
 */
export function getMainline(tree) {
  if (!tree || !tree.rootId) return [];
  const line = [];
  let cursor = tree.nodes[tree.rootId];
  while (cursor) {
    line.push(cursor);
    const firstChildId = cursor.childrenIds[0];
    if (!firstChildId) break;
    cursor = tree.nodes[firstChildId];
    if (!cursor) break;
  }
  return line;
}

/**
 * Hidrata un árbol a partir de un historial lineal existente (formato
 * ClassroomLive: `room.moves = [{ san, fen, from, to }, ...]`).
 *
 * El árbol resultante tiene UNA sola línea principal — sin variantes — y
 * currentId apunta al último nodo (equivalente a "live").
 *
 * @param {string}   startingFen
 * @param {object[]} moves
 * @returns {object}
 */
export function hydrateFromLinearMoves(startingFen, moves) {
  let tree = createTree(startingFen);
  if (!Array.isArray(moves)) return tree;
  let parentId = tree.rootId;
  for (const m of moves) {
    if (!m || typeof m.san !== 'string' || typeof m.fen !== 'string') {
      // Movimiento mal formado: se omite. No abortamos toda la hidratación
      // para mantener resiliencia frente a datos incompletos.
      continue;
    }
    tree = addMove(tree, parentId, {
      san: m.san,
      fen: m.fen,
      from: m.from,
      to: m.to,
      ...(m.promotion ? { promotion: m.promotion } : {}),
    });
    parentId = tree.currentId;
  }
  return tree;
}
